<template>
    <div
        class="Events-block noselect"
        v-click-outside="close"
    >
        <div
            class="Events-block-container"
            :class="{'Events-block-container_expanded': isEventsPanelExpanded}"
        
        >
            <div class="Events-block__section">
                <span class="Events-block__section-name">{{ showName }}:</span>
                <span
                    class="Events-block__no-filters"
                    v-if="!localEvents.length"
                >No {{ showName }}</span>
                <draggable
                    :list="localEventsOrderObjects"
                    @change="swapEvents"
                    :disabled="isDisableDraggable"
                    class="Events-block-draggable"
                >
                    <template v-for="(event,eventIndex) in localEventsOrderObjects">
                        <VLabel
                            :key="eventIndex"
                            :color="event.color"
                            :font-weight="event.fontWeight"
                            :disabled="!event.isActive"
                            @click="eventRemove(event.name)"
                        >{{ event.name }}
                        </VLabel>
                    </template>
                </draggable>
                <div
                    class="Events-block-more-text"
                    v-if="hiddenActiveEventsCount && !isEventsPanelExpanded"
                >
                    +{{ hiddenActiveEventsCount }} more
                </div>
            </div>
            <div
                class="Events-block__section Events-block__section-deleted"
                v-if="isEventsPanelExpanded"
            >
                <VLabel
                    v-for="(event,eventIndex) in deletedEventsWithoutDisabled"
                    :key="eventIndex"
                    :color="event.color"
                    :font-weight="event.fontWeight"
                    action="add"
                    @click="eventAdd(event)"
                >
                    {{ event.name }}
                </VLabel>
            </div>
            <div
                class="Events-block__section Events-block__section-deleted"
                v-if="isEventsPanelExpanded"
            >
                <VLabel
                    v-for="(event,eventIndex) in disabledEventsWithoutOrderEvents"
                    :key="eventIndex"
                    :color="event.color"
                    :font-weight="event.fontWeight"
                    action="add"
                    disabled
                    @mouseover="showTooltip($event,'text','Event is not available for this source')"
                >
                    {{ event.name }}
                </VLabel>
            </div>
            <div
                v-if="hiddenActiveEventsCount || deletedEvents.length || disabledEvents.length "
                class="Events-block__dropdown-arrow"
                @click="changeDropdownVisibility"
            >
            </div>
        </div>
    </div>
</template>

<script>
    import draggable from 'vuedraggable';
    import {ResizeObserver} from '@juggle/resize-observer';
    import {isIncludesAll} from '@/helpers/dataHelper';
    
    export default {
        name: 'Events-block',
        components: {
            draggable,
        },
        props: {
            events: {
                type: Array,
                required: true,
            },
            eventsOrder: {
                type: Array,
                required: true,
            },
            allEvents: {
                type: Array,
                required: true,
            },
            currentBoardAllEvents: {
                type: Array,
                required: true,
            },
            showName: {
                type: String,
                required: true,
                default: 'Events',
            },
            savedData: {
                type: Object,
                required: true,
            },
            currentSourceEvents: {
                type: Array,
                required: true,
            },
            tempCurrentSourceEventsNames: {
                type: Array,
                required: true,
            },
            sessionEventsOrder: {
                type: Array,
                required: true,
            },
        },
        data() {
            return {
                isEventsPanelExpanded: false,
                localEvents: [],
                localActiveAndDisabledEvents: [],
                localEventsOrderObjects: [],
                hiddenActiveEventsCount: 0,
                isDashboardWillBeUpdated: false,
            };
        },
        created() {
            this.initClearData();
        },
        mounted() {
            this.initEventsBlockObserver();
        },
        watch: {
            events: {
                deep: true,
                immediate: true,
                handler(newEvents) {
                    this.localEvents = _.cloneDeep(newEvents);
                    this.getCountOfHiddenEvents();
                },
            },
            eventsOrder: {
                deep: true,
                immediate: true,
                handler(newEvents) {
                    this.createLocalEventsOrderObjects(newEvents);
                },
            },
            currentSourceEvents: {
                deep: true,
                immediate: false,
                handler() {
                    this.createLocalEventsOrderObjects(this.eventsOrder);
                },
            },
            tempCurrentSourceEventsNames: {
                deep: true,
                immediate: false,
                handler(tempEvents) {
                    if (tempEvents.length) {
                        this.createLocalEventsOrderObjects(this.eventsOrder);
                    }
                },
            },
            activeAndDisabledEvents: {
                deep: true,
                immediate: true,
                handler(newEvents) {
                    this.localActiveAndDisabledEvents = _.cloneDeep(newEvents);
                    this.getCountOfHiddenEvents();
                },
            },
        },
        computed: {
            deletedEvents() {
                return this.allEvents.filter((allEvent) => (!this.localEvents.some((event) => event.name === allEvent.name)));
            },
            deletedEventsWithoutDisabled() {
                return this.deletedEvents.filter((deletedEvent) => !this.disabledEventsNames.includes(deletedEvent.name));
            },
            disabledEvents() {
                if (this.tempCurrentSourceEventsNames.length) {
                    return this.allEvents.filter((allEvent) => !this.tempCurrentSourceEventsNames.some((eventName) =>
                        eventName === allEvent.name));
                }
                return this.allEvents.filter((allEvent) => !this.currentSourceEvents.some((event) => event.name === allEvent.name));
            },
            disabledEventsWithoutOrderEvents() {
                return this.disabledEvents.filter((disabledEvent) => !this.eventsOrder.includes(disabledEvent.name));
            },
            disabledEventsNames() {
                return this.disabledEvents.map((event) => event.name);
            },
            isDisableDraggable() {
                return this.isDashboardWillBeUpdated;
            },
        },
        methods: {
            showTooltip(event, tooltipFunctionName, data) {
                this.$emit('showTooltip', event, tooltipFunctionName, data);
            },
            createLocalEventsOrderObjects(newEvents) {
                this.localEventsOrderObjects = _.cloneDeep(newEvents).map((eventName) => {
                    const event = this.allEvents.find((allEvent) => allEvent.name === eventName);
                    if (event) {
                        event.isActive = !this.disabledEventsNames.includes(eventName);
                    }
                    return event;
                }).filter((ev) => ev);
            },
            initEventsBlockObserver() {
                new ResizeObserver(this.getCountOfHiddenEvents).observe(document.querySelector('.Events-block-draggable'));
            },
            initClearData() {
                this.justDeletedEvents = [];
                this.justAddedEvents = [];
            },
            changeDropdownVisibility() {
                if (this.isEventsPanelExpanded) {
                    this.close();
                } else {
                    this.isEventsPanelExpanded = true;
                }
            },
            close() {
                if (this.isEventsPanelExpanded) {
                    this.isEventsPanelExpanded = false;
                    this.$emit('updateDashboardDataDebounced');
                    if (this.justDeletedEvents.length || this.justAddedEvents.length) {
                        this.$nextTick(() => {
                            this.$emit('updateEvents', this.localEvents, this.justDeletedEvents,
                                this.justAddedEvents, this.deletedEvents);
                            this.initClearData();
                            this.$nextTick(() => {
                                this.checkIsDashboardWillBeUpdated();
                            });
                        });
                    }
                }
            },
            eventRemove(eventName) {
                this.justDeletedEvents.push(eventName);
                this.localEvents = this.localEvents.filter((event) => event.name !== eventName);
                this.checkIsDashboardWillBeUpdated();
                this.$emit('update:eventsOrder', Object.freeze(this.eventsOrder.filter((eventName) =>
                    !this.justDeletedEvents.includes(eventName))));
                // this.$emit('update:sessionEventsOrder', Object.freeze(this.sessionEventsOrder.filter((eventName) =>
                //     !this.justDeletedEvents.includes(eventName))));
                if (!this.isEventsPanelExpanded) {
                    this.$emit('updateEvents', this.localEvents, this.justDeletedEvents, this.justAddedEvents,
                        this.deletedEvents);
                    this.initClearData();
                }
            },
            eventAdd(event) {
                this.justAddedEvents.push(event.name);
                const currentBoardAllSessionEventsOrder = this.sessionEventsOrder.findIndex((sessionEventName) => sessionEventName === event.name);
                let insertEventsOrderIndex = 0;
                for (let i = currentBoardAllSessionEventsOrder - 1; i >= 0; i--) {
                    const isEventWasDeletedDuringSession = this.eventsOrder.find((eventOrderName) => this.sessionEventsOrder[i] === eventOrderName);
                    if (isEventWasDeletedDuringSession) {
                        insertEventsOrderIndex = i + 1;
                        break;
                    }
                }
                const newEventsOrder = _.cloneDeep(this.eventsOrder);
                newEventsOrder.splice(insertEventsOrderIndex, 0, event.name);
                this.$emit('update:eventsOrder', Object.freeze(newEventsOrder));
                const eventsAllIndex = this.currentBoardAllEvents.findIndex((allEv) => allEv.name ===
                    event.name);
                let insertIndex;
                if (eventsAllIndex === 0 || !this.localEvents.length) {
                    insertIndex = 0;
                } else if (this.localEvents.length) {
                    for (let i = eventsAllIndex; i >= 0; i--) {
                        const index = this.localEvents.findIndex((event) => event.name ===
                            this.currentBoardAllEvents[i].name);
                        if (index >= 0) {
                            insertIndex = index + 1;
                            break;
                        }
                    }
                }
                this.localEvents.splice(insertIndex, 0, event);
                this.checkIsDashboardWillBeUpdated();
            },
            swapEvents(swapObject) {
                const firstIndex = swapObject.moved.oldIndex;
                const secondIndex = swapObject.moved.newIndex;
                this.$emit('swapEvents', firstIndex, secondIndex);
            },
            getCountOfHiddenEvents() {
                this.$nextTick(() => {
                    const eventsBlock = document.querySelector('.Events-block-draggable');
                    if (eventsBlock) {
                        const activeEventsElements = Array.from(eventsBlock.children);
                        if (activeEventsElements[0]) {
                            const eventsBlockTop = activeEventsElements[0].getBoundingClientRect().top;
                            this.hiddenActiveEventsCount = activeEventsElements.filter((el) =>
                                el.getBoundingClientRect().top !== eventsBlockTop).length;
                        }
                    }
                });
            },
            checkIsDashboardWillBeUpdated() {
                const eventsNames = this.localEvents.map((e) => e.name);
                this.isDashboardWillBeUpdated = !isIncludesAll(this.savedData.events, eventsNames);
            },
        },
    };
</script>

<style
    scoped
    lang="scss"
>
    .Events-block {
        position: relative;
        height: 38px;
        width: 100%;
        
        &-draggable {
            display: flex;
            flex-wrap: wrap;
            margin: -3.25px;
            
            ::v-deep .VLabel {
                margin: 6.5px;
            }
        }
        
        &-container {
            height: 38px;
            overflow: hidden;
            background: #ffffff;
            width: 100%;
            @include blue-shadow;
            border-radius: 5px;
            position: relative;
            z-index: 7;
            
            &_expanded {
                z-index: 10;
                height: auto;
                border: none;
                @include default-shadow;
                
                .Events-block__dropdown-arrow {
                    transform: rotate(180deg);
                }
            }
        }
        
        &__no-filters {
            padding: 6px 0px;
            margin-top: 3px;
        }
        
        &-more-text {
            color: #000F48;
            font-size: 14px;
            font-weight: 500;
            right: 50px;
            position: absolute;
            top: 12px;
        }
        
        &__section {
            display: flex;
            
            align-items: center;
            padding: 6px;
            padding-left: 80px;
            padding-right: 105px;
            flex-wrap: wrap;
            margin: -3.25px;
            
            ::v-deep .VLabel {
                margin: 6.5px;
            }
            
            &-deleted {
                margin: -8px;
            }
            
            &-name {
                position: absolute;
                left: 20px;
                top: 12px;
            }
        }
        
        &__dropdown-arrow {
            position: absolute;
            right: 18px;
            font-weight: bold;
            font-size: 15px;
            color: #800080;
            cursor: pointer;
            top: 13px;
            width: 12px;
            height: 12px;
            
            &::after {
                content: "\2039";
                color: #02DFB0;
                font-size: 23px;
                position: absolute;
                -webkit-transform: rotate(
                        -90deg
                );
                transform: rotate(
                        -90deg
                );
                font-weight: bold;
                left: 0;
                top: -5px;
            }
        }
    }
</style>
